import {
	canonicalDataSourceMetaKey,
	DATA_SOURCE_ICON_META,
} from '@jepto/shared/utils/data-source-icon-meta.util';

/**
 * Maps service slugs to SVGs under `assets/images/` — mirrors
 * `jep-service-icon.component.html` so analytics UI can show the same icons
 * without importing `JepServiceIconComponent` or touching `SharedModule`.
 *
 * Keep in sync when adding slugs to the shared `jep-service-icon` template.
 */
export interface AnalyticsServiceIconAsset {
	readonly src: string;
	readonly alt: string;
}

/** Slugs accepted by `jep-service-icon` / schema `primaryServiceSlugForSchemaField`. */
const ANALYTICS_SERVICE_ICON_ASSETS: Readonly<
	Record<string, AnalyticsServiceIconAsset>
> = {
	'google-analytics': {
		src: '/assets/images/google-analytics.svg',
		alt: 'Google Analytics',
	},
	'google-ads': {
		src: '/assets/images/google-ads.svg',
		alt: 'Google Ads',
	},
	'google-search-console': {
		src: '/assets/images/google-search-console.svg',
		alt: 'Google Search Console',
	},
	'google-my-business': {
		src: '/assets/images/google-my-business.svg',
		alt: 'Google Business Profile',
	},
	website: {
		src: '/assets/images/Website.svg',
		alt: 'Website',
	},
	web: {
		src: '/assets/images/Website.svg',
		alt: 'Website',
	},
	'facebook-ads': {
		src: '/assets/images/Facebook.svg',
		alt: 'Facebook',
	},
	sheets: {
		src: '/assets/images/GoogleSheets.svg',
		alt: 'Google Sheets',
	},
	'google-sheets': {
		src: '/assets/images/GoogleSheets.svg',
		alt: 'Google Sheets',
	},
	'google-drive': {
		src: '/assets/images/GoogleSheets.svg',
		alt: 'Google Sheets',
	},
	'microsoft-ads': {
		src: '/assets/images/Microsoft_Ads.svg',
		alt: 'Microsoft Ads',
	},
	'google-big-query': {
		src: '/assets/images/google-big-query.svg',
		alt: 'Google BigQuery',
	},
	'google-cloud-platform': {
		src: '/assets/images/google-cloud-platform.svg',
		alt: 'Google Cloud Platform',
	},
	'tiktok-ads': {
		src: '/assets/images/Tiktok.svg',
		alt: 'TikTok Ads',
	},
	'linkedin-ads': {
		src: '/assets/images/Linkedin.svg',
		alt: 'LinkedIn Ads',
	},
	'twitter-ads': {
		src: '/assets/images/Twitter.svg',
		alt: 'Twitter Ads',
	},
	'instagram-ads': {
		src: '/assets/images/Instagram.svg',
		alt: 'Instagram Ads',
	},
	'google-analytics4': {
		src: '/assets/images/GoogleAnalytics4.svg',
		alt: 'Google Analytics 4',
	},
	apple: {
		src: '/assets/images/Apple.svg',
		alt: 'Apple',
	},
	bing: {
		src: '/assets/images/Bing.svg',
		alt: 'Bing',
	},
	xero: {
		src: '/assets/images/Xero.svg',
		alt: 'Xero',
	},
	ai: {
		src: '/assets/images/Jepto-AI2.svg',
		alt: 'AI',
	},
};

/** Custom fields / custom connector slug use the code Material icon in analytics pickers. */
export const FORMULA_CUSTOM_METRIC_MATERIAL_ICON = 'code';

/** Unified / blended schema fields (sourceGroup `unified` → service slug `jepto`). */
export const UNIFIED_DATA_SOURCE_MATERIAL_ICON = 'join';

/** AI Visibility schema group (`aiVisibility` → service slug `ai-visibility`). */
export const AI_VISIBILITY_DATA_SOURCE_MATERIAL_ICON = 'visibility';

/** Service slugs that use a Material icon instead of a connector SVG in analytics pickers. */
const ANALYTICS_SERVICE_MATERIAL_ICON: Readonly<Record<string, string>> = {
	ai: FORMULA_CUSTOM_METRIC_MATERIAL_ICON,
	jepto: UNIFIED_DATA_SOURCE_MATERIAL_ICON,
	'ai-visibility': AI_VISIBILITY_DATA_SOURCE_MATERIAL_ICON,
};

export function resolveAnalyticsServiceIconAsset(
	service: string | null | undefined
): AnalyticsServiceIconAsset | undefined {
	if (service == null || service === '') {
		return undefined;
	}
	return ANALYTICS_SERVICE_ICON_ASSETS[service];
}

export function resolveAnalyticsServiceMaterialIcon(
	service: string | null | undefined
): string | undefined {
	if (service == null || service === '') {
		return undefined;
	}
	return ANALYTICS_SERVICE_MATERIAL_ICON[service];
}

/** Ensures formula metrics include connector keys for suggestion icons (not inferred from `name`). */
export function requireFormulaMetricSourceKeys(
	raw: readonly string[] | undefined,
	metricName: string
): readonly string[] {
	const keys = (raw ?? []).map(k => k.trim()).filter(Boolean);
	if (keys.length === 0) {
		throw new Error(
			`FormulaMetricOption "${metricName}" requires at least one sourceKey for connector icon resolution.`
		);
	}
	return keys;
}

/**
 * Autocomplete title for schema metrics: show the field segment only (e.g. `spend`),
 * since the connector icon identifies the source (`googleAds.spend` → `spend`).
 */
export function formulaMetricDisplayTitle(metricName: string): string {
	const trimmed = metricName.trim();
	const lastDot = trimmed.lastIndexOf('.');
	if (lastDot < 0 || lastDot === trimmed.length - 1) {
		return trimmed;
	}
	return trimmed.slice(lastDot + 1);
}

/** Cube / connector prefix before the first dot (e.g. `ds_abc` from `ds_abc.webClicks`). */
export function formulaMetricCubeName(metricName: string): string {
	const trimmed = metricName.trim();
	const dot = trimmed.indexOf('.');
	return dot < 0 ? trimmed : trimmed.slice(0, dot);
}

/** Per-connection external schema cubes (`ds_<uid>`). */
export function isPerConnectionSchemaCube(cubeName: string): boolean {
	return cubeName.trim().startsWith('ds_');
}

/**
 * Autocomplete row title for schema metrics. Appends the connection / client
 * label for per-connection cubes or when multiple metrics share the same short name.
 */
export function formulaMetricAutocompleteLabel(
	metricName: string,
	sourceLabel?: string | null,
	duplicateShortTitleCount = 1
): string {
	const short = formulaMetricDisplayTitle(metricName);
	const label = sourceLabel?.trim();
	if (!label) {
		return short;
	}
	const cube = formulaMetricCubeName(metricName);
	if (
		isPerConnectionSchemaCube(cube) ||
		duplicateShortTitleCount > 1
	) {
		return `${short} (${label})`;
	}
	return short;
}

/** First mappable `sourceKeys` entry → service slug for `resolveAnalyticsServiceIconAsset`. */
export function formulaMetricServiceSlug(
	sourceKeys: readonly string[]
): string | undefined {
	for (const key of sourceKeys) {
		const mk = canonicalDataSourceMetaKey(key);
		if (mk) {
			return DATA_SOURCE_ICON_META[mk].service;
		}
	}
	return undefined;
}

/** Canonical list of formula functions shown in autocomplete (keep in sync with `FormulaComponent`). */
export const FORMULA_SUPPORTED_FUNCTION_NAMES = [
	'CONCATENATE',
	'TITLECASE',
	'UPPERCASE',
	'LOWERCASE',
	'IF',
	'SUM',
	'AVG',
	'JSON_STRUCT',
] as const;

export type FormulaSupportedFunctionName =
	(typeof FORMULA_SUPPORTED_FUNCTION_NAMES)[number];

/** Material Symbols ligatures for formula functions (Figma autocomplete spec). */
export const FORMULA_FUNCTION_MATERIAL_ICON: Readonly<
	Record<FormulaSupportedFunctionName, string>
> = {
	CONCATENATE: 'join',
	TITLECASE: 'titlecase',
	UPPERCASE: 'uppercase',
	LOWERCASE: 'lowercase',
	IF: 'rule',
	SUM: 'add',
	AVG: 'percent',
	JSON_STRUCT: 'data_object',
};

export function formulaFunctionMaterialIcon(functionName: string): string {
	const icon =
		FORMULA_FUNCTION_MATERIAL_ICON[
			functionName as FormulaSupportedFunctionName
		];
	if (icon === undefined) {
		throw new Error(
			`Formula function "${functionName}" has no Material icon mapping in FORMULA_FUNCTION_MATERIAL_ICON`
		);
	}
	return icon;
}

export function formulaSuggestionMaterialIcon(entry: {
	type: 'function' | 'metric';
	name: string;
	isCustomMetric?: boolean;
}): string | undefined {
	if (entry.isCustomMetric) {
		return FORMULA_CUSTOM_METRIC_MATERIAL_ICON;
	}
	if (entry.type === 'function') {
		return formulaFunctionMaterialIcon(entry.name);
	}
	return undefined;
}

/** Custom fields in formula suggestions use the code Material icon, not a connector SVG. */
export function isCustomFormulaMetricName(name: string): boolean {
	const lower = name.trim().toLowerCase();
	return (
		lower.startsWith('custom.') ||
		lower.startsWith('custom_') ||
		lower.includes('.custom.')
	);
}
